import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './AuthModal.css';

const isNative = () => Boolean(window.Capacitor?.isNativePlatform());

const buildGoogleOAuthUrl = (clientId) => {
  const state = crypto.randomUUID();
  localStorage.setItem('pc_google_state', state);
  // Authorization-code flow: ?code= arrives as a query param so the backend
  // can read it, exchange for tokens, and issue a server-side 302 to poupix://.
  // Server-side redirects are the only way to open custom URL schemes from
  // SFSafariViewController; all client-side navigation attempts are blocked.
  const redirectUri = `${process.env.REACT_APP_API_BASE_URL}/auth/google/native/callback`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    state,
    access_type: 'online',
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
};

const AuthModal = ({ isOpen, onClose }) => {
  const [authMode, setAuthMode] = useState('login');
  const [loginValue, setLoginValue] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const googleBtnRef = useRef(null);
  const { user, isAuthenticated, login, register, loginWithGoogleToken, logout } = useAuth();
  const { t } = useI18n();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const handleNativeGoogleSignIn = async () => {
    if (!googleClientId) return;
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url: buildGoogleOAuthUrl(googleClientId) });
  };

  useEffect(() => {
    if (!isOpen || !googleClientId || isNative()) return;

    const scriptId = 'google-identity-script';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const setupGoogle = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          try {
            await loginWithGoogleToken(response.credential);
            onClose();
            setAuthError('');
          } catch (err) {
            const message = err?.response?.data?.detail || t('auth.googleFailed');
            setAuthError(message);
          }
        },
      });

      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
        });
      }
    };

    script.addEventListener('load', setupGoogle);
    if (window.google?.accounts?.id) setupGoogle();

    return () => {
      script.removeEventListener('load', setupGoogle);
    };
  }, [isOpen, googleClientId, loginWithGoogleToken, onClose, t]);

  if (!isOpen) return null;

  const submitAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      if (authMode === 'login') {
        await login({ login: loginValue, password });
      } else {
        await register({ username, email, password });
      }
      onClose();
      setPassword('');
    } catch (err) {
      const message = err?.response?.data?.detail || t('auth.authFailed');
      setAuthError(message);
    }
  };

  if (isAuthenticated) {
    return (
      <div className="auth-modal-overlay" onClick={onClose}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <div className="auth-modal-header">
            <h3>{t('auth.myAccount')}</h3>
            <button className="auth-close" onClick={onClose}>✕</button>
          </div>
          <div className="auth-account-info">
            <p className="auth-account-name">
              {t('auth.helloUser', { name: user?.display_name || user?.username || user?.email })}
            </p>
            <button className="auth-submit" onClick={() => { logout(); onClose(); }}>
              {t('header.logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h3>{authMode === 'login' ? t('header.login') : t('auth.createAccount')}</h3>
          <button className="auth-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={submitAuth} className="auth-form">
          {authMode === 'login' ? (
            <input
              type="text"
              placeholder={t('auth.usernameOrEmail')}
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value)}
              required
            />
          ) : (
            <>
              <input
                type="text"
                placeholder={t('auth.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <input
                type="email"
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </>
          )}

          <input
            type="password"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {authError && <p className="auth-error">{authError}</p>}

          <button type="submit" className="auth-submit">
            {authMode === 'login' ? t('header.login') : t('auth.signUp')}
          </button>

          {googleClientId && (
            isNative() ? (
              <button type="button" className="google-signin-native-btn" onClick={handleNativeGoogleSignIn}>
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.548 0 9s.348 2.825.957 4.039l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/></svg>
                {t('auth.continueWithGoogle')}
              </button>
            ) : (
              <div ref={googleBtnRef} className="google-signin-btn" />
            )
          )}
        </form>

        <button
          className="auth-toggle"
          onClick={() => setAuthMode((prev) => (prev === 'login' ? 'register' : 'login'))}
        >
          {authMode === 'login' ? t('auth.createAnAccount') : t('auth.haveAccountLogin')}
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
